import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Dialog, 
  DialogActions,
  DialogContent, 
  DialogTitle, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Box, 
  Typography,
} from '@mui/material';
import {
    ArrowForward as Next,
    ArrowBack as Back,
} from '@mui/icons-material';
import ButtonComponent from '../../../customButton';


// utilities
import RoleScopeEnum from '../../../../enums/RoleScopeEnum';
import { useSnackbar } from '../../../../utils/useSnackbar';
import { 
  resetFormData,
  setModules,
  setFormErrors,
  setFormData,
} from "../../../../features/roleStepper/roleStepperSlice";
import { getPermissionsByCategory } from "../../../../utils/api/permission_apis";
import { getRoleDetails, createRoleWithPermissions, updateRoleWithPermissions } from "../../../../utils/api/role_apis";
import { STATUS } from "../../../../utils/api/sharedfunction";

// components
import RoleNameForm from './RoleNameForm';
import RoleDisplayContainer from '../RoleDisplayContainer';
import RolePermissionSelector from './RolePermissionSelector';

const steps = ['Role Name', 'Permission', 'Confirmation'];

// const formatPermissionData = (data) => {
//   return data.reduce((acc, mod) => {
//     acc[mod.categoryId] = {
//       left: mod.permissionData,
//       right: [],
//       moduleName: mod.moduleName,
//     };
//     return acc;
//   }, {});
// };

// Utility function to format permission data
const formatPermissionData = (allPermissions, selectedPermissions = []) => {
  // Convert selectedPermissions into a Map for quick lookup
  const selectedMap = new Map();

  selectedPermissions.forEach(category => {
    selectedMap.set(category.categoryId, new Set(category.permissionData.map(p => p.id)));
  });

  // Format data into the required structure
  return allPermissions.reduce((acc, category) => {
    const categoryId = category.categoryId;
    const moduleName = category.moduleName;

    if (!acc[categoryId]) {
      acc[categoryId] = { left: [], right: [], moduleName };
    }

    category.permissionData.forEach(permission => {
      if (selectedMap.has(categoryId) && selectedMap.get(categoryId).has(permission.id)) {
        acc[categoryId].right.push(permission);
      } else {
        acc[categoryId].left.push(permission);
      }
    });

    return acc;
  }, {});
};

// Utility function to format the roleDetails into the desired payload structure
const formatRoleDetailsToPayload = (roleDetails) => {
  return {
    name: roleDetails.name,
    description: roleDetails.description,
    type: roleDetails.scope,
  };
};

// Function to determine the correct dialog title based on the actionType and roleType
const getDialogTitle = (actionType, roleType) => {
  const roleText = roleType === RoleScopeEnum.ORG_LEVEL ? 'Organization Role' : 'Project Role';
  if (actionType === 'create') {
    return `Create New - ${roleText}`;
  } else if (actionType === 'edit') {
    return `Edit - ${roleText}`;
  }
  return ''; 
};

// Function for permission formatter for API payload
const formatPermissions = (data) => {
  const permissions = [];
  for (const key in data) {
    if (data[key].right && data[key].right.length > 0) {
      permissions.push(...data[key].right);
    }
  }
  return permissions;
};


const StepperModal = ({ open, onClose, onConfirm }) => {

  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const actionType = useSelector((state) => state.roleStepper.actionType);
  const formData = useSelector((state) => state.roleStepper.formData);
  const modules = useSelector((state) => state.roleStepper.modules);
  const scopeType = useSelector((state) => state.roleStepper.roleType);
  const roleId = useSelector((state) => state.roleStepper.selectedRoleID);

  useEffect(() => {
     if (open) {
      setActiveStep(0); 
    }
    dispatch(resetFormData()); 
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchPermissionModules();
  }, [open, actionType, dispatch]);


  // API Calls

  const fetchPermissionModules = async () => {
    try {

      // Create
      if (actionType === "create") {
        const response = await getPermissionsByCategory({ scope: scopeType });

        if (response.status === STATUS.SUCCESS) {
          const formattedData = formatPermissionData(response.data);
          dispatch(setModules(formattedData));
        } else {
          console.error("Failed to fetch permissions:", response.error);
          showSnackbar(response.error || 'Failed to fetch permissions', 'error');
        }
      } 
      
      // Edit
      else if (actionType === "edit") {
        // Fetch all permissions
        const allPermissionsRes = await getPermissionsByCategory({ scope: scopeType });

        // Fetch role details (including selected permissions)
        const roleDetailsRes = await getRoleDetails(scopeType, roleId);

        if (allPermissionsRes.status === STATUS.SUCCESS && roleDetailsRes.status === STATUS.SUCCESS) {
          const allPermissions = allPermissionsRes.data;
          const roleDetails = roleDetailsRes.data.roleDetails;
          const selectedPermissions = roleDetailsRes.data.permissions;

          // Filter unselected permissions
          const formattedPermissions = formatPermissionData(allPermissions, selectedPermissions);
          const formattedRoleDetails = formatRoleDetailsToPayload(roleDetails);

          dispatch(setModules(formattedPermissions));
          dispatch(setFormData(formattedRoleDetails));
        } else {
          if (allPermissionsRes.status !== STATUS.SUCCESS) {
            console.error("Failed to fetch permissions:", allPermissionsRes.error);
            showSnackbar(allPermissionsRes.error || 'Failed to fetch permissions', 'error');
          }
          if (roleDetailsRes.status !== STATUS.SUCCESS) {
            console.error("Failed to fetch role details:", roleDetailsRes.error);
            showSnackbar(roleDetailsRes.error || 'Failed to fetch role details', 'error');
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch permission modules:", error);
      showSnackbar('Failed to fetch permission modules', 'error');
    }
  };

  const handleCreateRole = async (payload) => {
    const response = await createRoleWithPermissions(payload);
    if (response.status === STATUS.SUCCESS) {
      showSnackbar(response.message || 'Role created successfully', 'success');
    } else {
      console.error('Error creating role:', response.error);
      showSnackbar(response.error || 'Failed to create role', 'error');
    }
  };
  
  const handleUpdateRole = async (payload) => {
    const response = await updateRoleWithPermissions(roleId, payload);
    if (response.status === STATUS.SUCCESS) {
      showSnackbar(response.message || 'Role updated successfully', 'success');
    } else {
      console.error('Error updating role:', response.error);
      showSnackbar(response.error || 'Failed to update role', 'error');
    }
  };

  const validateRoleNameForm = () => {
    const errors = {
      name: false,
      description: false,
      nameMessage: "",
      descriptionMessage: "",
    };

    // Validate name
    if (!formData.name || formData.name.trim() === "") {
      errors.name = true;
      errors.nameMessage = "Role name is required";
    } else if (formData.name.trim().length < 1) {
      errors.name = true;
      errors.nameMessage = "Role name must be at least 1 character";
    } else if (formData.name.trim().length > 100) {
      errors.name = true;
      errors.nameMessage = "Role name must not exceed 100 characters";
    }

    // Validate description
    if (!formData.description || formData.description.trim() === "") {
      errors.description = true;
      errors.descriptionMessage = "Role description is required";
    } else if (formData.description.trim().length > 1000) {
      errors.description = true;
      errors.descriptionMessage = "Description must not exceed 1000 characters";
    }

    dispatch(setFormErrors(errors)); // Update form errors in Redux
    return !errors.name && !errors.description; // Return true if no errors
  };

  
  // Stepper navigation handlers

  const handleNext = () => {
    if (activeStep === 0) {
      const isValid = validateRoleNameForm();
      if (!isValid) return; 
    }
    setActiveStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleCancel = () => {
    onClose(); 
  };

  const handleConfirm = async () => {
    const permissions = formatPermissions(modules);
    const payload = {
      formData,
      permissions,
    };
  
    if (actionType === 'create') {
      await handleCreateRole(payload);
    } else if (actionType === 'edit') {
      await handleUpdateRole(payload);
    } else {
      console.warn('Unknown actionType:', actionType);
    }
  
    onConfirm(); 
    onClose();  
  };
  
  const handleClose = () => {
    setActiveStep(0);
    onClose();
  }

  // Determine the dialog title based on actionType and roleType
  const dialogTitle = getDialogTitle(actionType, scopeType);

  return (
    <Dialog 
     open={open} 
     onClose={handleClose} 
     maxWidth="md" 
     fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Stepper remains fixed at the top */}
        <Stepper 
         activeStep={activeStep} 
         alternativeLabel
         sx={{
          '& .MuiStepConnector-line': {
            borderColor: 'grey', 
            borderWidth: 1.5, 
          }
        }}
         >
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Scrollable content area */}
        <Box sx={{ mt: 2, overflowY: 'auto', maxHeight: '400px' }}>
          {activeStep === 0 && <RoleNameForm />}
          {activeStep === 1 && <RolePermissionSelector />}
          {activeStep === 2 && <RoleDisplayContainer />} 
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        {activeStep > 0 && (
          <ButtonComponent
            ctaText="Previous"
            onClick={handleBack} 
            variant="outline"
            className= 'primary'
            startIcon={<Back className='primary icon-medium'/>}
          />
        )}
        {activeStep < steps.length - 1 ? (
          <ButtonComponent
            ctaText="Next"
            onClick={handleNext} 
            className= 'white bg-blue600'
            endIcon={<Next className='white icon-medium'/>}
          />
        ) : (
          <ButtonComponent
            ctaText="Confirm"
            onClick={handleConfirm} 
            className= 'white bg-blue600'
          />
        )}
        <ButtonComponent
          ctaText="Cancel"
          onClick={handleCancel} 
          variant="outline"
          className= 'primary'
        />
      </DialogActions>
    </Dialog>
  );
}

export default StepperModal;
